import z from 'zod'
import { latLngSchema } from '../geography/types.ts'
import { translatableFieldSchema } from '../lib/languages.ts'

export const streetViewLocationTypeSchema = z.object({
  position: latLngSchema,
  heading: z.number(),
  pitch: z.number(),
  zoom: z.number(),
})

export const buildingTypeSchema = z.union([
  z.literal('apartment').describe('An apartment in a multi-unit and multi-floor building.'),
  z.literal('detached').describe('A detached house.'),
  z.literal('duplex').describe('A duplex is a building with two units.'),
  z
    .literal('row')
    .describe('A row of units attached along their sidewalls, also called terrace houses'),
  z
    .literal('farmhouse')
    .describe('A detached house on an unzoned plot, usually in the countryside'),
  z.literal('other').describe('Other building type not listed above'),
])

export type BuildingType = z.infer<typeof buildingTypeSchema>

export const assetConditionSchema = z.union([
  z
    .literal('new')
    .describe(
      'A property that was built during the current year or is currently being built, where the buyer would be the first owner.'
    ),
  z
    .literal('excellent')
    .describe('A property that is in excellent condition, but has had a previous owner.'),
  z.literal('good').describe('A property that is in good condition.'),
  z.literal('fair').describe('A property that is in fair condition.'),
  z.literal('bad').describe('A property that is in bad condition but is still livable.'),
  z
    .literal('unlivable')
    .describe('A property that is in bad condition and is not livable without major renovations.'),
])

export type AssetCondition = z.infer<typeof assetConditionSchema>

export const unitTypeEnum = z
  .union([
    z
      .literal('residential')
      .describe(
        'The default unit type. If the property is residential, this should be used for the main or only unit/building.'
      ),
    z
      .literal('guest')
      .describe(
        'Guest house or other secondary residential unit or building. Must not be used as the type of the only unit/building in the property.'
      ),
    z.literal('garage').describe('Garage or parking space included in the property.'),
    z
      .literal('service')
      .describe('Service building or other auxiliary building or unit included in the property.'),
    z
      .literal('storage')
      .describe('Storage building such as a shed or other outbuilding included in the property.'),
    z
      .literal('apartment')
      .describe(
        'An apartment that is included in the property. Can be type of the main unit, or it can be an additional apartment that is part of the same property.'
      ),
    z.literal('commercial').describe('Commercial unit or building included in the property.'),
    z.literal('other').describe('Other unit or building included in the property.'),
  ])
  .describe('Describes the type of a unit or building included in the property.')

export const assetRenovationSchema = z.object({
  year: z.string().or(z.number()),
  description: z.string(),
})

export type AssetRenovation = z.infer<typeof assetRenovationSchema>

export const unitTypeSchema = z.object({
  type: unitTypeEnum,
  specifier: z.string().nullish().describe('Name or other specifier for the unit.'),
  rooms: z.number().nullish().describe('Number of rooms in the unit.'),
  size: z.number().nullish().describe('Size of the unit in square meters.'),
  bathrooms: z.number().optional().describe('Number of bathrooms in the unit.'),
  kitchens: z.number().optional().describe('Number of kitchens in the unit.'),
  entrances: z.number().optional().describe('Number of entrances to the unit.'),
  floorCount: z.number().optional().describe('Total number of floors in the unit.'),
  onFloor: z
    .number()
    .optional()
    .describe('Floor number which the unit is on. Not applicable for non-apartment unit types.'),
  buildYear: z.number().optional().describe('Year the unit was built (finished).'),
  condition: assetConditionSchema.optional(),
})

export type UnitType = z.infer<typeof unitTypeSchema>

export const lotOwnershipSchema = z.enum(['own', 'lease', 'unknown'])
export type LotOwnership = z.infer<typeof lotOwnershipSchema>

export const propertyDataSchema = z.object({
  streetViewLocation: streetViewLocationTypeSchema.optional(),
  propertyType: buildingTypeSchema.describe(
    'Building type of the property. Generally refers to the main unit.'
  ),
  units: z
    .array(unitTypeSchema)
    .optional()
    .describe(
      'Distinct units in the property. A unit is a distinct building or apartment share that is included in the sale. This also counts outbuildings, sheds, garages, etc. All units should be on the same address. Any unit specifier in the address should reference the main unit. Traits apply to the whole property.'
    ),
  description: z
    .string()
    .or(translatableFieldSchema)
    .nullish()
    .describe('Description of the property.'),
  lot: z
    .object({
      size: z.number().nullish().describe('Size of the lot in square meters.'),
      ownership: lotOwnershipSchema.nullish().describe('Ownership of the lot.'),
    })
    .nullish(),
  renovations: z
    .array(assetRenovationSchema)
    .nullish()
    .optional()
    .describe('Renovations done on the property.'),
  bump: z.string().optional().describe('Just a value to save and trigger the update event.'),
})

export const assetDataSchema = z
  .object({
    version: z
      .union([z.literal('1')])
      .optional()
      .default('1'),
  })
  .and(propertyDataSchema)

export type AssetDataType = z.infer<typeof assetDataSchema>

export const assetMediaSchema = z.object({
  url: z.string(),
  type: z.enum(['image', 'video', 'document']).default('image'),
  description: z.string().or(translatableFieldSchema).nullish(),
  order: z.number().nullish().default(0),
  storageId: z.string().nullish().optional(),
  storagePath: z.string().nullish(),
  blurHash: z.string().nullish().optional(),
  hash: z.string().nullish().optional(),
})

export type AssetMedia = z.infer<typeof assetMediaSchema>
